import xhrService from '../xhrService';

/**
 * SW2-2572 — saving one photo's markup on its own (the annotation editor's Save), coordinated with
 * the finding save:
 *
 * - Saves of one photo go one at a time, in the order they were made, so the last edit is the one
 *   the server keeps.
 * - A finding save waits for them (`markupSavesSettled`), then sends each of a photo's saved marks
 *   with the id the server gave it (`withSavedMarkupIds`). The finding save inserts every mark without an id,
 *   so marks it captured before their ids came back would otherwise be added twice.
 */

export type Annotation = { id?: number; feature_data: string };

/** a photo's markup, ignoring row ids, so a saved copy compares equal to what was sent */
export const markupOf = (annotations: Annotation[] = []) =>
  JSON.stringify(annotations.map((annotation) => annotation.feature_data));

// photo id → the last save queued for it (never rejects), until it settles
const queues = new Map<number, Promise<unknown>>();
// photo id → the rows the server last confirmed: all of the photo's marks, as a save replaces them
const confirmed = new Map<number, Annotation[]>();

const put = (
  findingId: number,
  uploadId: number,
  annotations: Annotation[],
): Promise<Required<Annotation>[]> =>
  xhrService
    .put(`/api/findings/${findingId}/uploads/${uploadId}/annotations`, {
      annotations: annotations.map(({ feature_data }) => ({ feature_data })),
    })
    .then((response: any) => response.data);

/**
 * Replaces one saved photo's markup with exactly `annotations`, after any save of it already
 * queued, and resolves with the saved rows (with ids). `uploadId` is the finding upload's id.
 */
export function saveMarkup(
  findingId: number,
  uploadId: number,
  annotations: Annotation[],
): Promise<Required<Annotation>[]> {
  const previous = queues.get(uploadId) || Promise.resolve();
  const save = previous.then(() => put(findingId, uploadId, annotations));
  const settled = save.then(
    (rows) => {
      confirmed.set(uploadId, rows);
    },
    () => {},
  );

  queues.set(uploadId, settled);
  settled.then(() => {
    if (queues.get(uploadId) === settled) queues.delete(uploadId);
  });

  return save;
}

/** resolves once every markup save started so far has finished, whether or not it succeeded */
export function markupSavesSettled(): Promise<void> {
  return Promise.all(Array.from(queues.values())).then(() => undefined);
}

/**
 * `upload` with each of its marks given the id of a row the server confirmed with the same data,
 * and any other mark sent as new (no id). A finding save leaves marks with ids alone and adds the
 * rest, so this keeps it from adding a saved mark again.
 *
 * Mark by mark, not the whole drawing: when a save succeeds and a newer edit queued behind it
 * fails, the form holds the newer drawing, whose saved marks still need their ids. And a save
 * replaces every row, so an id from before it would make the finding save skip a mark the server
 * no longer has.
 */
export function withSavedMarkupIds<T extends { id?: number; annotations?: Annotation[] }>(
  upload: T,
): T {
  const rows = typeof upload.id === 'number' ? confirmed.get(upload.id) : undefined;

  if (!rows) return upload;

  // each saved row stands for one mark, so a mark drawn twice is matched twice
  const unmatched = [...rows];
  const annotations = (upload.annotations || []).map(({ feature_data }) => {
    const at = unmatched.findIndex((row) => row.feature_data === feature_data);

    return at < 0 ? { feature_data } : unmatched.splice(at, 1)[0];
  });

  return { ...upload, annotations };
}
